import { ref, shallowRef, watch, type ShallowRef } from 'vue';
import type { SftpClient } from '@ts-ssh2/core';
import { type Client, type DirectoryEntry, SftpError, STATUS_CODE } from '@ts-ssh2/core';

export type FileNode = {
	name: string;
	path: string;
	isDirectory: boolean;
	isSymlink: boolean;
	symlinkTarget: string | null;
	children: FileNode[] | null; // null = not loaded yet
	loading: boolean;
	expanded: boolean;
};

export type OpenFile = {
	path: string;
	name: string;
	content: string;
	dirty: boolean;
	saving: boolean;
};

export type ExpandedPathStore = {
	addExpandedPath: (path: string) => void;
	removeExpandedPath: (path: string) => void;
	isExpanded: (path: string) => boolean;
};

export type RootPathStore = {
	getRootPath: () => string | null;
	setRootPath: (path: string) => void;
};

export function useSftp(
	clientRef: ShallowRef<Client | null>,
	expandedStore: ExpandedPathStore,
	rootPathStore?: RootPathStore,
) {
	const sftp = shallowRef<SftpClient | null>(null);
	const rootNode = ref<FileNode | null>(null);
	const rootPath = ref<string>('/');
	const openFiles = ref<OpenFile[]>([]);
	const activeFilePath = ref<string | null>(null);
	const error = ref<string | null>(null);
	const initializing = ref(false);

	// stat a path and build a FileNode (follows same symlink logic as loadDirectory)
	async function statNode(path: string): Promise<FileNode> {
		const name = path === '/' ? '/' : path.split('/').pop()!;
		const lstatResult = await sftp.value!.lstat(path);
		const isSymlink = lstatResult.isSymbolicLink();
		let isDirectory = lstatResult.isDirectory();
		let symlinkTarget: string | null = null;

		if (isSymlink) {
			const [targetResult, statResult] = await Promise.allSettled([
				sftp.value!.readlink(path),
				!isDirectory ? sftp.value!.stat(path) : Promise.resolve(null),
			]);
			if (targetResult.status === 'fulfilled') {
				symlinkTarget = targetResult.value;
			}
			if (statResult.status === 'fulfilled' && statResult.value) {
				isDirectory = statResult.value.isDirectory();
			}
		}

		return {
			name,
			path,
			isDirectory,
			isSymlink,
			symlinkTarget,
			children: isDirectory ? null : [],
			loading: false,
			expanded: false,
		};
	}

	async function loadRootNode(path: string) {
		const node = await statNode(path);
		// root always starts expanded with its children loaded
		if (node.isDirectory) {
			node.expanded = true;
			node.loading = true;
			const childrenHolder = { value: [] as FileNode[] };
			await loadDirectory(path, childrenHolder);
			node.children = childrenHolder.value;
			node.loading = false;
		}
		rootNode.value = node;
	}

	// initialize sftp session when client connects, tear down when it disconnects
	watch(clientRef, async (newClient, oldClient) => {
		if (oldClient && sftp.value) {
			sftp.value.dispose();
			sftp.value = null;
			rootNode.value = null;
			openFiles.value = [];
			activeFilePath.value = null;
			// don't clear expandedStore — paths persist across reconnects
		}

		if (newClient) {
			initializing.value = true;
			error.value = null;
			try {
				const session = await newClient.sftp();
				sftp.value = session;

				// use persisted root path, or resolve home directory
				const persisted = rootPathStore?.getRootPath();
				const home = persisted ?? (await session.realpath('.'));
				rootPath.value = home;
				rootPathStore?.setRootPath(home);

				await loadRootNode(home);

				// restore previously-expanded directories
				const children = rootNode.value?.children ?? [];
				const toExpand = children.filter((n) => n.isDirectory && expandedStore.isExpanded(n.path));
				await Promise.all(toExpand.map((n) => expandNode(n)));
			} catch (err) {
				error.value = err instanceof Error ? err.message : String(err);
			} finally {
				initializing.value = false;
			}
		}
	});

	async function loadDirectory(dirPath: string, parentChildren: { value: FileNode[] }) {
		if (!sftp.value) return;

		try {
			const entries: DirectoryEntry[] = await sftp.value.readdir(dirPath);

			const filtered = entries.filter((e) => e.filename !== '.' && e.filename !== '..');

			// resolve symlinks to determine whether they point to directories
			const resolved = await Promise.all(
				filtered.map(async (entry) => {
					const isSymlink = entry.attrs.isSymbolicLink();
					let isDirectory = entry.attrs.isDirectory();
					let symlinkTarget: string | null = null;

					if (isSymlink) {
						const path = dirPath === '/' ? `/${entry.filename}` : `${dirPath}/${entry.filename}`;
						const [targetResult, statResult] = await Promise.allSettled([
							sftp.value!.readlink(path),
							!isDirectory ? sftp.value!.stat(path) : Promise.resolve(null),
						]);
						if (targetResult.status === 'fulfilled') {
							symlinkTarget = targetResult.value;
						}
						if (statResult.status === 'fulfilled' && statResult.value) {
							isDirectory = statResult.value.isDirectory();
						}
					}

					return { entry, isSymlink, isDirectory, symlinkTarget };
				}),
			);

			const nodes: FileNode[] = resolved
				.sort((a, b) => {
					// directories first, then alphabetical
					if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
					return a.entry.filename.localeCompare(b.entry.filename);
				})
				.map(({ entry, isSymlink, isDirectory, symlinkTarget }) => ({
					name: entry.filename,
					path: dirPath === '/' ? `/${entry.filename}` : `${dirPath}/${entry.filename}`,
					isDirectory,
					isSymlink,
					symlinkTarget,
					children: isDirectory ? null : [],
					loading: false,
					expanded: false,
				}));

			parentChildren.value = nodes;
		} catch (err) {
			error.value = err instanceof Error ? err.message : String(err);
		}
	}

	async function expandNode(node: FileNode) {
		node.loading = true;
		node.expanded = true;
		const oldChildren = node.children;
		const childrenHolder = { value: [] as FileNode[] };
		await loadDirectory(node.path, childrenHolder);

		// transfer expanded subtree state from old children so they stay
		// visible while we refetch deeper levels in the background
		if (oldChildren && oldChildren.length > 0) {
			const oldByPath = new Map<string, FileNode>();
			for (const child of oldChildren) {
				oldByPath.set(child.path, child);
			}
			for (const newChild of childrenHolder.value) {
				const old = oldByPath.get(newChild.path);
				if (old && old.isDirectory && old.expanded && old.children) {
					newChild.expanded = true;
					newChild.children = old.children;
				}
			}
		}

		node.children = childrenHolder.value;
		node.loading = false;

		// recursively re-expand children that were previously expanded
		const childExpands = node.children
			.filter((child) => child.isDirectory && expandedStore.isExpanded(child.path))
			.map((child) => expandNode(child));
		await Promise.all(childExpands);
	}

	async function toggleDirectory(node: FileNode) {
		if (!node.isDirectory) return;

		if (node.expanded) {
			node.expanded = false;
			expandedStore.removeExpandedPath(node.path);
			return;
		}

		expandedStore.addExpandedPath(node.path);
		await expandNode(node);
	}

	async function refreshDirectory(node: FileNode) {
		if (!node.isDirectory) return;
		await expandNode(node);
	}

	// wait for sftp to be ready (e.g. when panels restore before session init)
	function waitForSftp(timeoutMs = 10_000): Promise<SftpClient | null> {
		if (sftp.value) return Promise.resolve(sftp.value);

		return new Promise<SftpClient | null>((resolve) => {
			const timeout = setTimeout(() => {
				stop();
				resolve(null);
			}, timeoutMs);

			const stop = watch(sftp, (val) => {
				if (val) {
					clearTimeout(timeout);
					stop();
					resolve(val);
				}
			});
		});
	}

	// read a file as raw bytes with progress reporting
	// onProgress receives 0-100 as chunks are read
	async function readFile(
		path: string,
		onProgress?: (percent: number) => void,
	): Promise<Uint8Array | null> {
		// wait for sftp if not ready yet (panels can restore before session init)
		const session = sftp.value ?? (await waitForSftp());
		if (!session) return null;

		try {
			// stat to get file size for progress reporting
			const attrs = await session.stat(path);
			const totalSize = attrs.size;

			// use parallel reads for faster loading
			const stream = session.createReadStream(path);
			const reader = stream.getReader();
			const chunks: Uint8Array[] = [];
			let bytesRead = 0;

			for (;;) {
				const { done, value } = await reader.read();
				if (done) break;
				chunks.push(value);
				bytesRead += value.byteLength;
				if (totalSize > 0 && onProgress) {
					onProgress(Math.min(100, Math.round((bytesRead / totalSize) * 100)));
				}
			}

			return chunks.length === 1 ? chunks[0]! : concatChunks(chunks, bytesRead);
		} catch (err) {
			if (err instanceof SftpError && err.code === STATUS_CODE.PERMISSION_DENIED) {
				error.value = `Permission denied: ${path}`;
			} else {
				error.value = err instanceof Error ? err.message : String(err);
			}
			return null;
		}
	}

	// open a file by path alone — TextEditorPanel calls this to load content
	// onProgress receives 0-100 as chunks are read
	async function openFilePath(
		path: string,
		onProgress?: (percent: number) => void,
	): Promise<boolean> {
		const existing = openFiles.value.find((f) => f.path === path);
		if (existing) {
			activeFilePath.value = path;
			return true;
		}

		const bytes = await readFile(path, onProgress);
		if (!bytes) return false;

		const name = path.split('/').pop() ?? path;
		const content = new TextDecoder().decode(bytes);

		openFiles.value.push({
			path,
			name,
			content,
			dirty: false,
			saving: false,
		});
		activeFilePath.value = path;
		return true;
	}

	function concatChunks(chunks: Uint8Array[], totalLength: number): Uint8Array {
		const result = new Uint8Array(totalLength);
		let offset = 0;
		for (const chunk of chunks) {
			result.set(chunk, offset);
			offset += chunk.byteLength;
		}
		return result;
	}

	function updateFileContent(path: string, content: string) {
		const file = openFiles.value.find((f) => f.path === path);
		if (file) {
			file.content = content;
			file.dirty = true;
		}
	}

	async function saveFile(path: string, onProgress?: (percent: number) => void) {
		const file = openFiles.value.find((f) => f.path === path);
		if (!file || !sftp.value) return;

		file.saving = true;
		try {
			const encoded = new TextEncoder().encode(file.content);
			const stream = sftp.value.createWriteStream(path);
			const writer = stream.getWriter();

			const CHUNK_SIZE = 1024 * 1024; // 1 MiB chunks for progress granularity
			let bytesWritten = 0;

			for (let offset = 0; offset < encoded.byteLength; offset += CHUNK_SIZE) {
				const chunk = encoded.subarray(offset, Math.min(offset + CHUNK_SIZE, encoded.byteLength));
				await writer.write(chunk);
				bytesWritten += chunk.byteLength;
				if (onProgress) {
					onProgress(Math.min(100, Math.round((bytesWritten / encoded.byteLength) * 100)));
				}
			}

			// handle empty files
			if (encoded.byteLength === 0) {
				await writer.write(encoded);
				onProgress?.(100);
			}

			await writer.close();
			file.dirty = false;
		} catch (err) {
			error.value = err instanceof Error ? err.message : String(err);
		} finally {
			file.saving = false;
		}
	}

	function closeFile(path: string) {
		const index = openFiles.value.findIndex((f) => f.path === path);
		if (index === -1) return;

		openFiles.value.splice(index, 1);

		// if we closed the active file, activate the nearest remaining tab
		if (activeFilePath.value === path) {
			if (openFiles.value.length === 0) {
				activeFilePath.value = null;
			} else {
				const nextIndex = Math.min(index, openFiles.value.length - 1);
				activeFilePath.value = openFiles.value[nextIndex]!.path;
			}
		}
	}

	async function changeRoot(newRoot: string) {
		if (!sftp.value) return;

		initializing.value = true;
		error.value = null;
		try {
			rootPath.value = newRoot;
			rootPathStore?.setRootPath(newRoot);
			rootNode.value = null;

			await loadRootNode(newRoot);

			// restore previously-expanded directories under the new root
			const node = rootNode.value as FileNode | null;
			const children = node?.children ?? [];
			const toExpand = children.filter((n) => n.isDirectory && expandedStore.isExpanded(n.path));
			await Promise.all(toExpand.map((n) => expandNode(n)));
		} catch (err) {
			error.value = err instanceof Error ? err.message : String(err);
		} finally {
			initializing.value = false;
		}
	}

	function clearError() {
		error.value = null;
	}

	return {
		sftp,
		rootNode,
		rootPath,
		openFiles,
		activeFilePath,
		error,
		initializing,
		toggleDirectory,
		refreshDirectory,
		readFile,
		openFilePath,
		updateFileContent,
		saveFile,
		closeFile,
		changeRoot,
		clearError,
	};
}
